import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PublicInvoice,
  AuthedInvoice,
  getPublicInvoice,
  getInvoiceByTokenAuthed,
} from '../services/invoiceService';
import { config } from '../config/environment';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getClientPaymentMethod, chargeClient } from '../services/paymentService';
import { buildSymbolMap, currencySymbol, FALLBACK_SYMBOL_MAP, SymbolMap } from '../utils/currency';

// ── Stripe payment form (guest) ────────────────────────────────────────────────

function GuestPaymentForm({ invoice, token, symbolMap }: { invoice: PublicInvoice; token: string; symbolMap: SymbolMap }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setPaying(true);
    setError('');
    try {
      // Create PaymentIntent via backend
      const authToken = localStorage.getItem('authToken');
      const res = await fetch(`${config.apiBaseUrl}/payments/invoice/${token}/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Payment failed' }));
        throw new Error(err.detail || 'Payment failed');
      }
      const { client_secret } = await res.json();
      const cardEl = elements.getElement(CardElement);
      if (!cardEl) throw new Error('Card element not ready');
      const { error: stripeErr } = await stripe.confirmCardPayment(client_secret, {
        payment_method: { card: cardEl },
      });
      if (stripeErr) throw new Error(stripeErr.message || 'Payment failed');
      setSuccess(true);
    } catch (e: any) {
      setError(e.message || 'Payment failed');
    } finally { setPaying(false); }
  };

  if (success) {
    return (
      <div className="text-center py-6">
        <div className="text-4xl mb-3">✓</div>
        <p className="text-green-400 font-semibold text-lg">Payment successful!</p>
        <p className="text-gray-400 text-sm mt-1">You'll receive a receipt by email.</p>
      </div>
    );
  }

  if (!stripe) {
    return <p className="text-sm text-gray-400 py-2">Loading payment form…</p>;
  }

  return (
    <div className="space-y-4">
      <div className="p-3 bg-dark-canvas border border-dark-border rounded-lg">
        <CardElement options={{ style: { base: { color: '#e5e7eb', '::placeholder': { color: '#6b7280' } } } }} />
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
      <button
        disabled={paying}
        onClick={handlePay}
        className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg disabled:opacity-50 transition-colors"
      >
        {paying ? 'Processing…' : `Pay ${currencySymbol(invoice.currency, symbolMap)}${(invoice.amount_cents / 100).toFixed(2)}`}
      </button>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

const InvoicePublicPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState<PublicInvoice | AuthedInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthed, setIsAuthed] = useState(false);
  const [symbolMap, setSymbolMap] = useState<SymbolMap>(FALLBACK_SYMBOL_MAP);

  useEffect(() => {
    fetch(`${config.apiBaseUrl}/countries`)
      .then(r => r.ok ? r.json() : [])
      .then((countries: any[]) => setSymbolMap(buildSymbolMap(countries)))
      .catch(() => {});
  }, []);
  const [isCoach, setIsCoach] = useState(false);
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);
  const [coachCardLast4, setCoachCardLast4] = useState<string | null>(null);
  const [coachCardBrand, setCoachCardBrand] = useState<string | null>(null);
  const [coachCharging, setCoachCharging] = useState(false);
  const [coachChargeError, setCoachChargeError] = useState('');
  const [coachChargeSuccess, setCoachChargeSuccess] = useState(false);

  useEffect(() => {
    if (!token) return;
    const jwt = localStorage.getItem('authToken');
    const userType = localStorage.getItem('userType');

    const fetchInvoice = async () => {
      try {
        if (jwt && userType === 'client') {
          try {
            const authed = await getInvoiceByTokenAuthed(token);
            setInvoice(authed);
            setIsAuthed(true);
            if (authed.stripe_enabled && authed.stripe_publishable_key) {
              const opts = authed.stripe_account_id ? { stripeAccount: authed.stripe_account_id } : undefined;
              setStripePromise(loadStripe(authed.stripe_publishable_key, opts));
            }
            return;
          } catch {
            // Fall through to public path if auth fails
          }
        }
        const pub = await getPublicInvoice(token);
        setInvoice(pub);
        setIsAuthed(false);
        if (pub.stripe_enabled && pub.stripe_publishable_key) {
          const opts = pub.stripe_account_id ? { stripeAccount: pub.stripe_account_id } : undefined;
          setStripePromise(loadStripe(pub.stripe_publishable_key, opts));
        }
        // Coach viewing their own invoice — fetch saved card for client
        if (jwt && userType === 'coach' && pub.client_id) {
          setIsCoach(true);
          try {
            const cardData = await getClientPaymentMethod(pub.client_id);
            if (cardData.has_card) {
              setCoachCardLast4(cardData.card_last4 ?? null);
              setCoachCardBrand(cardData.card_brand ?? null);
            }
          } catch { /* ignore — no card */ }
        }
      } catch (e: any) {
        if (e.message?.includes('410') || e.message?.includes('expired')) {
          setError('This invoice link has expired. Please log in to your client portal to view receipts.');
        } else if (e.message?.includes('404') || e.message?.includes('not found')) {
          setError('Invoice not found.');
        } else {
          setError('Unable to load invoice. Please try again.');
        }
      } finally { setLoading(false); }
    };
    fetchInvoice();
  }, [token]);

  const authedInvoice = isAuthed ? (invoice as AuthedInvoice) : null;
  const hasSavedCard = authedInvoice && authedInvoice.saved_card_last4;

  const handleCoachCharge = async () => {
    if (!invoice?.appointment_id) return;
    setCoachCharging(true);
    setCoachChargeError('');
    try {
      await chargeClient(invoice.appointment_id);
      setCoachChargeSuccess(true);
    } catch (e: any) {
      setCoachChargeError(e.message || 'Charge failed');
    } finally {
      setCoachCharging(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <p className="text-gray-400">Loading invoice…</p>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <p className="text-gray-300 text-lg mb-2">{error || 'Invoice not found.'}</p>
          <button
            onClick={() => navigate('/dashboard/client')}
            className="mt-4 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go to Client Portal
          </button>
        </div>
      </div>
    );
  }

  const amount = (invoice.amount_cents / 100).toFixed(2);
  const sym = currencySymbol(invoice.currency, symbolMap);
  const isPaid = invoice.status === 'paid';
  const dueStr = invoice.due_date
    ? new Date(invoice.due_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : null;
  const paidOnStr = invoice.paid_at
    ? new Date(invoice.paid_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="min-h-screen bg-dark-bg flex items-start justify-center p-4 pt-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-2xl font-bold text-white">CoachSight ECR</p>
          <p className="text-sm text-gray-400 mt-1">Coaching Invoice</p>
        </div>

        {/* Invoice card */}
        <div className="bg-dark-card border border-dark-border rounded-2xl shadow-xl overflow-hidden mb-6">
          <div className="bg-indigo-900/30 px-6 py-5 border-b border-dark-border">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Invoice</p>
                <p className="text-white font-bold text-lg">{invoice.invoice_number}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400">From</p>
                <p className="text-gray-200 font-medium">{invoice.coach_name}</p>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Client</span>
              <span className="text-gray-200">{invoice.client_name}</span>
            </div>
            {invoice.session_date && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Session</span>
                <span className="text-gray-200">
                  {new Date(invoice.session_date).toLocaleDateString()}
                  {invoice.session_duration_minutes && ` · ${invoice.session_duration_minutes} min`}
                </span>
              </div>
            )}
            {invoice.description && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Description</span>
                <span className="text-gray-200 text-right max-w-[60%]">{invoice.description}</span>
              </div>
            )}
            {dueStr && !isPaid && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Due</span>
                <span className="text-gray-200">{dueStr}</span>
              </div>
            )}
            <div className="border-t border-dark-border pt-3 flex justify-between items-center">
              <span className="text-gray-300 font-medium">
                {isPaid ? 'Amount Paid' : 'Amount Due'}
              </span>
              <span className="text-white font-bold text-xl">{sym}{amount}</span>
            </div>
            {isPaid && (
              <div className="flex items-center justify-between bg-green-900/30 border border-green-800/50 rounded-lg px-3 py-2">
                <span className="text-green-400 text-sm font-semibold">✓ Paid</span>
                {paidOnStr && <span className="text-green-300 text-xs">{paidOnStr}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Payment section */}
        {!isPaid && (
          <div className="bg-dark-card border border-dark-border rounded-2xl shadow-xl overflow-hidden mb-6">
            <div className="px-6 py-5">
              {invoice.payment_status === 'failed' && (
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-700/50 bg-red-900/20 px-4 py-3">
                  <span className="text-red-400 text-base mt-0.5">✕</span>
                  <div>
                    <p className="text-sm font-semibold text-red-300">Payment failed</p>
                    {invoice.payment_failure_reason && (
                      <p className="text-xs text-red-400/80 mt-0.5">{invoice.payment_failure_reason}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">Please try again with a different card or contact your coach.</p>
                  </div>
                </div>
              )}
              <p className="text-sm font-semibold text-gray-200 mb-4">{isCoach ? 'Charge Client' : (invoice.payment_status === 'failed' ? 'Try Again' : 'Pay Now')}</p>

              {/* Coach viewing their own invoice — charge client's saved card */}
              {isCoach && (
                coachChargeSuccess ? (
                  <div className="text-center py-4">
                    <div className="text-3xl mb-2">✓</div>
                    <p className="text-green-400 font-semibold">Payment charged successfully!</p>
                  </div>
                ) : coachCardLast4 ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-dark-canvas border border-dark-border rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm text-gray-200 font-medium">
                          {coachCardBrand ? `${coachCardBrand.charAt(0).toUpperCase()}${coachCardBrand.slice(1)} ending ` : 'Card ending '}
                          {coachCardLast4}
                        </p>
                        <p className="text-xs text-gray-500">Client's saved card</p>
                      </div>
                    </div>
                    {coachChargeError && <p className="text-xs text-red-400">{coachChargeError}</p>}
                    <button
                      disabled={coachCharging || !invoice.appointment_id}
                      onClick={handleCoachCharge}
                      className="w-full py-3 bg-green-700 hover:bg-green-600 text-white font-semibold rounded-lg disabled:opacity-50 transition-colors"
                    >
                      {coachCharging ? 'Charging…' : `Charge ${sym}${amount} to saved card`}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg border border-yellow-800/40 bg-yellow-900/20 px-3 py-3">
                    <span className="text-yellow-400 text-sm mt-0.5">⚠</span>
                    <p className="text-xs text-yellow-300">
                      No saved card for this client. The client needs to pay via this invoice link.
                    </p>
                  </div>
                )
              )}

              {/* Not logged in + Stripe available — offer saved-card login */}
              {!isCoach && !isAuthed && !localStorage.getItem('authToken') && invoice.stripe_enabled && (
                <div className="mb-4 p-3 bg-indigo-900/20 border border-indigo-800/40 rounded-lg text-xs text-indigo-300">
                  Have a saved card?{' '}
                  <a
                    href={`/?redirect=/invoice/${token}`}
                    className="underline hover:text-indigo-100"
                  >
                    Log in to use it →
                  </a>
                </div>
              )}

              {/* Saved card one-click (logged in client) */}
              {!isCoach && isAuthed && hasSavedCard && authedInvoice && (
                <SavedCardPayment invoice={authedInvoice} token={token!} symbolMap={symbolMap} />
              )}

              {/* Stripe card entry (client) */}
              {!isCoach && invoice.stripe_enabled && stripePromise && (!isAuthed || !hasSavedCard) && (
                <Elements stripe={stripePromise}>
                  <GuestPaymentForm invoice={invoice} token={token!} symbolMap={symbolMap} />
                </Elements>
              )}

              {/* Stripe enabled but key not returned — config gap */}
              {!isCoach && invoice.stripe_enabled && !stripePromise && (!isAuthed || !hasSavedCard) && (
                <div className="flex items-start gap-2 rounded-lg border border-yellow-800/40 bg-yellow-900/20 px-3 py-3">
                  <span className="text-yellow-400 text-sm mt-0.5">⚠</span>
                  <p className="text-xs text-yellow-300">
                    Payment is not available at the moment. Please contact your coach directly to arrange payment.
                  </p>
                </div>
              )}

              {!isCoach && !invoice.stripe_enabled && (
                <div className="flex items-start gap-2 rounded-lg border border-yellow-800/40 bg-yellow-900/20 px-3 py-3">
                  <span className="text-yellow-400 text-sm mt-0.5">⚠</span>
                  <p className="text-xs text-yellow-300">
                    Your coach hasn't connected a payment account yet. Please contact them directly to arrange payment.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {invoice.notes && (
          <div className="bg-dark-card border border-dark-border rounded-xl px-5 py-4">
            <p className="text-xs text-gray-400 mb-1">Note from coach</p>
            <p className="text-sm text-gray-200">{invoice.notes}</p>
          </div>
        )}

        <p className="text-center text-xs text-gray-600 mt-6">Powered by CoachSight ECR</p>
      </div>
    </div>
  );
};

// ── Saved card one-click payment ───────────────────────────────────────────────

function SavedCardPayment({ invoice, token, symbolMap }: { invoice: AuthedInvoice; token: string; symbolMap: SymbolMap }) {
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [useNew, setUseNew] = useState(false);
  const [stripePromise] = useState(() => {
    if (!invoice.stripe_publishable_key) return null;
    const opts = invoice.stripe_account_id ? { stripeAccount: invoice.stripe_account_id } : undefined;
    return loadStripe(invoice.stripe_publishable_key, opts);
  });
  const handleOneClick = async () => {
    setPaying(true);
    setError('');
    try {
      const authToken = localStorage.getItem('authToken');
      const res = await fetch(`${config.apiBaseUrl}/payments/invoice/${token}/charge-saved-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Payment failed' }));
        throw new Error(err.detail || 'Payment failed');
      }
      setSuccess(true);
    } catch (e: any) {
      setError(e.message || 'Payment failed');
    } finally { setPaying(false); }
  };

  if (success) {
    return (
      <div className="text-center py-4">
        <div className="text-3xl mb-2">✓</div>
        <p className="text-green-400 font-semibold">Payment successful!</p>
        <p className="text-gray-400 text-sm mt-1">You'll receive a receipt by email.</p>
      </div>
    );
  }

  if (useNew && stripePromise) {
    return (
      <div>
        <button onClick={() => setUseNew(false)} className="text-xs text-gray-400 hover:text-gray-200 mb-3">← Use saved card</button>
        <Elements stripe={stripePromise}>
          <GuestPaymentForm invoice={invoice} token={token} symbolMap={symbolMap} />
        </Elements>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 p-3 bg-dark-canvas border border-dark-border rounded-lg">
        <div className="flex-1">
          <p className="text-sm text-gray-200 font-medium">
            {invoice.saved_card_brand?.charAt(0).toUpperCase()}{invoice.saved_card_brand?.slice(1)} ending {invoice.saved_card_last4}
          </p>
          <p className="text-xs text-gray-500">Saved card</p>
        </div>
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
      <button
        disabled={paying}
        onClick={handleOneClick}
        className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg disabled:opacity-50 transition-colors"
      >
        {paying ? 'Processing…' : `Pay ${currencySymbol(invoice.currency, symbolMap)}${(invoice.amount_cents / 100).toFixed(2)} with saved card`}
      </button>
      <button onClick={() => setUseNew(true)} className="w-full text-xs text-gray-500 hover:text-gray-300 py-1">
        Use a different card
      </button>
    </div>
  );
}

export default InvoicePublicPage;
